import type { AppData, Period, Sale } from "@/types";
import { receivableBalance } from "@/utils/accounting";
import { pvForPeriod } from "@/utils/pv";

export interface AmwayLevel {
  pct: number;
  label: string;
  minPv: number;
  nextMinPv: number | null;
  nextPct: number | null;
  progress: number; // 0 - 100 hacia el siguiente nivel
}

// Escala de calificación Amway (3% a 21%) por Puntos de Volumen.
export const AMWAY_LEVELS: { pct: number; minPv: number }[] = [
  { pct: 3, minPv: 300 },
  { pct: 6, minPv: 600 },
  { pct: 9, minPv: 1200 },
  { pct: 12, minPv: 2400 },
  { pct: 15, minPv: 4000 },
  { pct: 18, minPv: 7000 },
  { pct: 21, minPv: 10000 },
];

export const getAmwayLevel = (pv: number): AmwayLevel => {
  let current = { pct: 0, minPv: 0 };
  for (const lvl of AMWAY_LEVELS) {
    if (pv >= lvl.minPv) current = lvl;
  }
  const next = AMWAY_LEVELS.find((l) => l.minPv > pv) ?? null;
  const nextMinPv = next ? next.minPv : null;
  const span = next ? next.minPv - current.minPv : 0;
  const progress = next && span > 0
    ? Math.min(100, Math.max(0, ((pv - current.minPv) / span) * 100))
    : 100;
  return {
    pct: current.pct,
    label: current.pct === 0 ? "Sin calificar" : `${current.pct}%`,
    minPv: current.minPv,
    nextMinPv,
    nextPct: next ? next.pct : null,
    progress,
  };
};

export const saleMatchesPeriod = (sale: Sale, period: Period): boolean => {
  const d = new Date(sale.date);
  if (Number.isNaN(d.getTime())) return false;
  if (d.getFullYear() !== period.year) return false;
  if (period.month === "anual") return true;
  return d.getMonth() === period.month;
};

// Ingreso que le corresponde al administrador en una venta.
// · Venta directa (admin): el total completo.
// · Venta de un asesor: el total menos el descuento del asesor (su margen),
//   que es lo que el administrador realmente factura al asesor.
export const adminRecognizedRevenue = (sale: Sale): number =>
  sale.advisorId === "admin"
    ? sale.total
    : sale.source === "propio"
    ? 0
    : Math.round(sale.total * (1 - sale.marginPct / 100));

// Ganancia real de una venta = precio cobrado según el canal − costo base.
// Evita el doble descuento que se aplicaba al multiplicar por 0.25 el ingreso
// ya neto del asesor.
// · Venta directa (admin): costo real de compra del producto.
// · Venta de asesor: base reposición = 75% del precio público (regla comercial),
//   así una venta al 25% da 0 y una al 20% da +5%.
export const saleRealProfit = (sale: Sale, data: AppData): number => {
  // La venta de mercancía propia del asesor no le deja ingreso ni ganancia al
  // administrador: esa mercancía nunca fue suya.
  if (sale.advisorId !== "admin" && sale.source === "propio") return 0;
  const product = data.products.find((p) => p.id === sale.productId);
  const unitCost =
    sale.advisorId === "admin"
      ? product?.costBase ?? 0
      : Math.round((product?.pricePublic ?? 0) * 0.75);
  return adminRecognizedRevenue(sale) - unitCost * sale.qty;
};

export const inventoryValue = (data: AppData): number =>
  data.inventory.reduce((acc, item) => {
    const product = data.products.find((p) => p.id === item.productId);
    if (!product) return acc;
    const units =
      item.stockCasa +
      item.stockPuntoClear +
      Object.values(item.stockByAdvisor).reduce((s, n) => s + n, 0);
    return acc + units * product.pricePublic;
  }, 0);

export interface Kpis {
  totalSales: number;
  grossProfit: number;
  totalPv: number; // PV que califica (compras de stock del período + referidos)
  stockPv: number; // PV de las compras de stock del período
  referralPv: number; // puntos de referidos (manual)
  level: AmwayLevel;
  inventoryTotal: number;
  baseCapital: number;
  estimatedProfit: number;
  receivablePending: number;
  salesCount: number;
  lowRotation: number;
}

export const computeKpis = (data: AppData, period: Period): Kpis => {
  const periodSales = data.sales.filter((s) => saleMatchesPeriod(s, period));
  const totalSales = periodSales.reduce((acc, s) => acc + adminRecognizedRevenue(s), 0);
  // Los PV ya no salen de las ventas: se acumulan al ENTRAR mercancía al stock
  // (cada compra) y se reinician cada mes. Los referidos suman a la calificación.
  const pv = pvForPeriod(data, period);
  const inventoryTotal = inventoryValue(data);
  const grossProfit = periodSales.reduce((acc, s) => acc + saleRealProfit(s, data), 0);
  const receivablePending = data.receivables.reduce(
    (acc, r) => acc + receivableBalance(r),
    0
  );

  return {
    totalSales,
    grossProfit,
    totalPv: pv.qualifyingPv,
    stockPv: pv.stockPv,
    referralPv: pv.referralPv,
    level: getAmwayLevel(pv.qualifyingPv),
    inventoryTotal,
    baseCapital: inventoryTotal * 0.75,
    estimatedProfit: inventoryTotal * 0.25,
    receivablePending,
    salesCount: periodSales.length,
    lowRotation: data.products.filter((p) => {
      const item = data.inventory.find((i) => i.productId === p.id);
      if (!item) return false;
      const units =
        item.stockCasa +
        item.stockPuntoClear +
        Object.values(item.stockByAdvisor).reduce((s, n) => s + n, 0);
      return units > 0;
    }).length,
  };
};

export const distributionAmounts = (
  grossProfit: number,
  dist: { donation: number; advisor: number; reinvestment: number; management: number }
) => ({
  donation: grossProfit * (dist.donation / 100),
  advisor: grossProfit * (dist.advisor / 100),
  reinvestment: grossProfit * (dist.reinvestment / 100),
  management: grossProfit * (dist.management / 100),
});